// MACHINERY V2 (Stroje) — catalog browse query: the filter set, the reset rules
// and the ACCURATE active-filter count (M2.8).
//
// Spec: data package §11, the "Catalog filters + prefill" field table and its
// "Counts/relationships" paragraph. Reference impl:
// components/CatalogBrowsePage.tsx (query state :245-248, activeFilterCount
// :388-400, handleClearFilters :460-482).
//
// >>> §9 D6 — THE FOUR DEAD PARAMS ARE NOW REAL FILTERS. <<<
// `minTools` / `maxTools` / `controlSystemBrandId` / `operationTypeId` have
// always existed on `CatalogBrowseQuery` and have always been spread into the
// request (machineCatalogSlice.ts `browseCatalogModels` destructures
// `...filters` and hands them to catalogApi.searchModels). They had NO UI, and
// v1's count was wrong in BOTH directions:
//   · it counted controlSystemBrandId + operationTypeId, which no control could
//     set — data package §11 calls them "dead-but-count-affecting";
//   · it did NOT count minTools/maxTools at all.
// CatalogFilterRail now gives all four real inputs, so the count below counts
// exactly the filters a user can actually set — no more, no less — and
// `clearCatalogFilters` resets exactly what the count counts. Those two lists
// are kept adjacent on purpose: a filter that can be counted but not cleared is
// the defect this pairing prevents.
//
// SERVER SUPPORT VERIFIED END TO END (process rule (a), read in
// GoToStockServer-machinery at this session's checkout):
//   CustomerCatalogController.cs:51-55 passes MinTools/MaxTools/
//   ControlSystemBrandId/OperationTypeId into ICatalogService.SearchModelsAsync
//   (:41);
//   CatalogService.cs:43 forwards them to the repository;
//   CatalogModelsRepository.cs:96 (`ToolCapacity >= minTools`), :101
//   (`ToolCapacity <= maxTools`), :111 (`ControlSystemBrandId ==`) and :116
//   (`OperationTypeId ==`) apply them as real predicates.
// So this is FRONTEND-ONLY work: no endpoint, DTO or query change was needed.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { CatalogBrowseQuery } from "../../models/models";

export const CATALOG_PAGE_SIZES = [20, 50, 100];

export const CATALOG_DEFAULT_QUERY: CatalogBrowseQuery = {
  page: 1,
  pageSize: CATALOG_PAGE_SIZES[0],
};

/**
 * Every filter the rail/tabs/search can set, in ONE list. `clearCatalogFilters`
 * blanks it and `catalogActiveFilterCount` walks it, so the two can never drift.
 * `categoryCode` is deliberately absent: the tabs are a scope, not a filter —
 * v1 leaves the tab selection alone on "Clear all" too (:460-482) and the count
 * has never included it.
 */
const FILTER_KEYS = [
  "search",
  "manufacturerId",
  "minRpm",
  "maxRpm",
  "minHp",
  "maxHp",
  "minTools",
  "maxTools",
  "taperTypeId",
  "controlSystemBrandId",
  "operationTypeId",
  "oemReady",
  "machineIdIntegrated",
] as const;

/**
 * A min/max pair counts as ONE filter (data package §11: "counts as ONE
 * filter"), so the ranges are folded before the singles are counted.
 */
const RANGE_PAIRS: [keyof CatalogBrowseQuery, keyof CatalogBrowseQuery][] = [
  ["minRpm", "maxRpm"],
  ["minHp", "maxHp"],
  ["minTools", "maxTools"],
];

const SINGLE_KEYS: (keyof CatalogBrowseQuery)[] = [
  "search",
  "manufacturerId",
  "taperTypeId",
  "controlSystemBrandId",
  "operationTypeId",
  "oemReady",
  "machineIdIntegrated",
];

/**
 * Counts exactly: search · manufacturer · RPM range · HP range · TOOLS range ·
 * taper · control-system brand · operation type · OEM ready · MachineID
 * integrated = at most 10. Every one of those has a control in
 * CatalogFilterRail (or the header search box); nothing else is counted.
 */
export const catalogActiveFilterCount = (query: CatalogBrowseQuery): number => {
  let count = 0;
  RANGE_PAIRS.forEach(([lo, hi]) => {
    if (query[lo] !== undefined || query[hi] !== undefined) count++;
  });
  SINGLE_KEYS.forEach((key) => {
    if (query[key]) count++;
  });
  return count;
};

/** "Clear all" — resets every counted filter and returns to page 1. The tab
 *  scope and the page size survive, exactly as they do in v1. */
export const clearCatalogFilters = (query: CatalogBrowseQuery): CatalogBrowseQuery => {
  const next: CatalogBrowseQuery = { ...query, page: 1 };
  FILTER_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

/**
 * Any filter change resets to page 1 (data package §11: "Reset to 1 on any
 * filter change"). Page and page-size changes go through `setCatalogPage` /
 * `setCatalogPageSize` instead, so this helper can reset unconditionally.
 */
export const withCatalogFilter = (
  query: CatalogBrowseQuery,
  patch: Partial<CatalogBrowseQuery>
): CatalogBrowseQuery => ({ ...query, ...patch, page: 1 });

/** A category tab change ALSO drops the manufacturer facet (the facet list is
 *  refetched per category, so the old id is usually absent from it) — v1's
 *  handleCategorySelect :403-416. The brand search box is client-side state and
 *  is cleared by the caller alongside this. */
export const withCatalogCategory = (
  query: CatalogBrowseQuery,
  categoryCode?: string
): CatalogBrowseQuery => ({
  ...query,
  categoryCode,
  manufacturerId: undefined,
  page: 1,
});

export const setCatalogPage = (
  query: CatalogBrowseQuery,
  page: number
): CatalogBrowseQuery => ({ ...query, page });

export const setCatalogPageSize = (
  query: CatalogBrowseQuery,
  pageSize: number
): CatalogBrowseQuery => ({ ...query, pageSize, page: 1 });

/** Blank input -> undefined (the param is then omitted from the request, which
 *  is what "no bound" means to the API). v1 uses parseInt/parseFloat the same
 *  way; NaN is filtered so a half-typed "-" cannot be sent. */
export const parseRangeInt = (raw: string): number | undefined => {
  if (!raw.trim()) return undefined;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const parseRangeFloat = (raw: string): number | undefined => {
  if (!raw.trim()) return undefined;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * DR-1 / DR-2 OMIT-WHEN-UNCHECKED. The readiness booleans are tri-state on the
 * wire: `true` filters, ABSENT does not, and `false` would be sent as a real
 * value the backend then treats as a filter. v1's two toggles (:481-483 and
 * :486-492) both flip to `undefined` rather than `false` for exactly this
 * reason — never write `false` here.
 */
export const toggleCatalogFlag = (
  query: CatalogBrowseQuery,
  key: "oemReady" | "machineIdIntegrated"
): CatalogBrowseQuery => withCatalogFilter(query, { [key]: query[key] ? undefined : true });
